import {
	centsToDollars,
	isDateBetween,
	ResultOrError,
	roundNumber,
	Timestamp,
} from '@marmon/shared/types/common';
import {
	CalculationEntityType,
	Invoice,
	InvoicePlot,
	InvoicePlotKey,
	InvoiceRollup,
	InvoiceRollupWithInvoices,
	ParseInvoiceError,
	RawInvoice,
	RawInvoiceKey,
	RawInvoiceKeys,
	TimePeriod,
} from '../interfaces';
import { generateMargin } from './analysis.utils';
import { RawInvoiceFormatterMap } from './invoice-formatter.utils';

export type FormatRawInvoiceResult = {
	invoices: Invoice[];
	minDate: Date;
	maxDate: Date;
	products: string[];
	customers: string[];
	segments: string[];
};

type ConvertedInvoice = Pick<
	Invoice,
	| 'costInCents'
	| 'invoiceDate'
	| 'revenueInCents'
	| 'customer'
	| 'product'
	| 'segment'
>;

const InvoiceIdFingerprintSeparator = '\u001f';

type InvoiceIdSource = Pick<
	Invoice,
	'customer' | 'product' | 'segment' | 'costInCents' | 'revenueInCents'
> & {
	invoiceDate: Pick<Timestamp, 'toMillis'>;
};

/**
 * Stable Firestore document id for an invoice row.
 * Fingerprint is customer, product, segment, invoice date, cost, revenue,
 * and the zero-based CSV row index, hashed with FNV-1a 64-bit.
 *
 * Import and CSV-sourced calculations (`csvStoragePath`) both call this.
 * Changing the fingerprint breaks updates onto existing invoice docs.
 * `csvIndex` must be the original file row even when segments are filtered.
 */
export function createInvoiceId(
	invoice: InvoiceIdSource,
	csvIndex: number,
): string {
	const fingerprint = [
		invoice.customer.trim(),
		invoice.product.trim(),
		(invoice.segment ?? '').trim(),
		invoice.invoiceDate.toMillis(),
		invoice.costInCents,
		invoice.revenueInCents,
		csvIndex,
	].join(InvoiceIdFingerprintSeparator);

	return fnv1a64Hex(fingerprint);
}

function fnv1a64Hex(value: string): string {
	let hash = BigInt('0xcbf29ce484222325');
	const prime = BigInt('0x100000001b3');
	const mask = BigInt('0xffffffffffffffff');
	for (let i = 0; i < value.length; i++) {
		hash ^= BigInt(value.charCodeAt(i));
		hash = (hash * prime) & mask;
	}
	return hash.toString(16).padStart(16, '0');
}

export type RollupEntity = keyof Pick<Invoice, 'product' | 'customer'>;

const ConverterMap = (): {
	[key in RawInvoiceKey]: keyof ConvertedInvoice;
} => ({
	Cost: 'costInCents',
	Customer: 'customer',
	'Invoice Date': 'invoiceDate',
	'Part #': 'product',
	Revenue: 'revenueInCents',
	Segment: 'segment',
});

function createInvoice(
	raw: RawInvoice,
	options: { createdAt: Timestamp; row: number; csvIndex: number },
): ResultOrError<Invoice, ParseInvoiceError> {
	const errors: ParseInvoiceError[] = [];
	const { row, createdAt, csvIndex } = options;

	let converted!: ConvertedInvoice;

	const formatters = RawInvoiceFormatterMap();
	const map = ConverterMap();
	for (const rawKey of RawInvoiceKeys) {
		const formatter = formatters[rawKey];
		if (!formatter) {
			console.error(
				'The formatter could not be determined for rawKey with the available keys',
				{ rawKey, all: Object.keys(formatters) },
			);

			errors.push({
				type: 'InsufficientData',
				code: 'InvalidValue',
				message: `Invalid key value: The formatter could not be determined for "${rawKey}"`,
				messageHtml: `Invalid key value: The formatter could not be determined for "<b>${rawKey}</b>"`,
				row,
			});
			continue;
		}

		formatter(
			raw[rawKey] ?? '',
			{
				error: (err) => errors.push(err),
				complete: (value) => {
					converted = { ...converted, [map[rawKey]]: value };
				},
			},
			{ row },
		);
	}

	if (errors.length) return { errors };

	const { valueInCents, percentage } = generateMargin(
		centsToDollars(converted.revenueInCents),
		centsToDollars(converted.costInCents),
	);

	const now = createdAt;
	return {
		result: {
			deletedAt: null,
			createdAt: now,
			updatedAt: now,
			id: createInvoiceId(converted, csvIndex),
			csvIndex,
			segment: converted.segment,
			product: converted.product,
			customer: converted.customer,
			costInCents: converted.costInCents,
			invoiceDate: converted.invoiceDate,
			revenueInCents: converted.revenueInCents,
			marginInCents: valueInCents,
			marginPercent: percentage,
			calculations: [],
			quads: {},
		},
	};
}

/**
 * @param options.csvIndexes Original zero-based CSV rows aligned with
 * `rawInvoices`. Required when the array was filtered (calculation CSV path)
 * so `createInvoiceId()` matches docs written at import.
 */
export function formatRawInvoices(
	rawInvoices: RawInvoice[],
	options: { now: Timestamp; csvIndexes?: number[] },
): ResultOrError<FormatRawInvoiceResult, ParseInvoiceError> {
	const totalErrors: ParseInvoiceError[] = [];

	// if we assign date values to an object, they will ASC sort automatically,
	// to avoid having to do another sort iteration.
	// key, rows - rows needed for future on better error handling
	const timePeriodsMap = new Map<number, number[]>();
	const products = new Set<string>();
	const customers = new Set<string>();
	const segments = new Set<string>();

	const invoices: Invoice[] = [];
	const { now, csvIndexes } = options;
	for (let i = 0; i < rawInvoices.length; i++) {
		const raw = rawInvoices[i];
		const csvIndex = csvIndexes?.[i] ?? i;

		// offset the header row. Use original csvIndex so filtered parses
		// still report the source row and keep deterministic ids aligned.
		const row = csvIndex + 1;

		const { errors, result } = createInvoice(raw, {
			createdAt: now,
			row,
			csvIndex,
		});
		if (errors?.length) totalErrors.push(...errors);
		if (errors?.length || !result) continue;

		const millis = result.invoiceDate.toMillis();
		if (timePeriodsMap.has(millis)) {
			const rows = timePeriodsMap.get(millis) as number[];
			timePeriodsMap.set(millis, [...rows, row]);
		} else {
			timePeriodsMap.set(millis, [row]);
		}

		products.add(result.product);
		customers.add(result.customer);

		// segment could be null/not provided
		segments.add(result.segment ?? null);

		invoices.push(result);
	}

	// sort the time periods from older to newer to ensure we are getting correct min and max
	const timePeriods = Array.from(timePeriodsMap.keys()).sort((a, b) => a - b);

	const min = timePeriods[0];
	if (Number.isNaN(min)) {
		totalErrors.push({
			code: 'InvalidValue',
			type: 'InsufficientData',
			message:
				'Invalid value: The minimum date detected did not match the value expected',
		});
	}

	const max = timePeriods[timePeriods.length - 1];
	if (Number.isNaN(max)) {
		totalErrors.push({
			code: 'InvalidValue',
			type: 'InsufficientData',
			message:
				'Invalid value: The maximum date detected did not match the value expected',
		});
	}

	if (min === max) {
		totalErrors.push({
			code: 'TooFewTimePeriods',
			type: 'InsufficientData',
			message:
				'Too few time periods: Only one unique Invoice Date could be determined, when multiple are required.',
		});
	} else if (!min || !max) {
		totalErrors.push({
			type: 'InsufficientData',
			code: 'InvalidValue',
			message:
				'Invalid values: The minimum and maximum dates could not be determined',
		});
	}

	if (totalErrors.length) return { errors: totalErrors };

	return {
		result: {
			invoices,
			minDate: new Date(min),
			maxDate: new Date(max),
			products: Array.from(products.values()),
			customers: Array.from(customers.values()),
			segments: Array.from(segments.values()),
		},
	};
}

const getPeriodIndex = (timePeriods: TimePeriod[], date: Timestamp) =>
	timePeriods.findIndex((period) =>
		isDateBetween(date.seconds, {
			min: period.minDate.seconds,
			max: period.maxDate.seconds,
		}),
	);

export function getTimePeriod(invoice: Invoice, timePeriods: TimePeriod[]) {
	const period = getPeriodIndex(timePeriods, invoice.invoiceDate);
	if (period < 0) {
		console.error(
			`A time period could not be determined: ${invoice.invoiceDate.toDate().toISOString()}`,
		);
	}

	return {
		period,
		data: timePeriods[period],
	};
}

type ListMap<T> = Map<string, T>;

/**
 * The createReference needs to be provided because the client and the server have different
 * api's that create the reference. Since we need to use this on both ends, this is the best
 * solution to make it reusable without needing to copy similar code, or iterating through the
 * long list of invoices again.
 */
export function formatToLists(invoices: Invoice[]): {
	[key in CalculationEntityType]: ListMap<InvoiceRollupWithInvoices>;
} & { plots: ListMap<InvoicePlot> } {
	const products = new Map<string, InvoiceRollupWithInvoices>();
	const customers = new Map<string, InvoiceRollupWithInvoices>();
	const plots = new Map<InvoicePlotKey, InvoicePlot>();

	const rollup = (
		key: RollupEntity,
		list: ListMap<InvoiceRollupWithInvoices>,
		invoice: Invoice,
	) => {
		const value = createInvoiceRollup(invoice, key);
		determineInvoiceRollup(invoice, list, key, value);
	};

	const plot = (invoice: Invoice) => {
		const value = createInvoicePlot(invoice);
		determineInvoicePlot(invoice, plots, value);
	};

	invoices.forEach((invoice) => {
		plot(invoice);
		rollup('customer', customers, invoice);
		rollup('product', products, invoice);
	});

	return { products, customers, plots };
}

function createInvoicePlot(invoice: Invoice): InvoicePlot<Timestamp> {
	const { valueInCents, percentage } = generateMargin(
		centsToDollars(invoice.revenueInCents),
		centsToDollars(invoice.costInCents),
	);

	return {
		id: crypto.randomUUID(),
		coordinates: [0, 0], // updated in calculations
		customer: invoice.customer,
		product: invoice.product,
		revenueInCents: invoice.revenueInCents,
		costInCents: invoice.costInCents,
		marginInCents: valueInCents,
		marginPercent: percentage,
	};
}

function createInvoiceRollup(
	invoice: Invoice,
	key: RollupEntity,
): InvoiceRollupWithInvoices {
	const { valueInCents, percentage } = generateMargin(
		centsToDollars(invoice.revenueInCents),
		centsToDollars(invoice.costInCents),
	);

	return {
		id: crypto.randomUUID(),
		product: key === 'product' ? invoice.product : null,
		customer: key === 'customer' ? invoice.customer : null,
		segment: invoice.segment,
		costInCents: invoice.costInCents,
		revenueInCents: invoice.revenueInCents,
		marginInCents: valueInCents,
		marginPercent: percentage,
		transactionAvgInCents: invoice.revenueInCents,
		transactionCount: 1,
		invoices: [invoice.id],
		...DefaultInvoiceRollupEt(),
	};
}

/**
 * Determines what to do with the new rollup, either add it to an existing name from the list
 * or start a new entry in the list.
 */
function determineInvoiceRollup(
	invoice: Invoice,
	map: Map<string, InvoiceRollup>,
	key: RollupEntity,
	rollup: InvoiceRollupWithInvoices,
) {
	// key value could be null, so nullish values should be evaluated as empty strings here
	const keyValue = invoice[key] ?? '';
	if (map.has(keyValue)) {
		const existing = map.get(keyValue) as InvoiceRollupWithInvoices;
		map.set(keyValue, addToInvoiceRollup(existing, invoice));
	} else {
		map.set(keyValue, rollup);
	}
}

/**
 * Determines what to do with the new rollup, either add it to an existing name from the list
 * or start a new entry in the list.
 */
function determineInvoicePlot(
	invoice: Invoice,
	map: Map<InvoicePlotKey, InvoicePlot>,
	plot: InvoicePlot,
) {
	const key: InvoicePlotKey = `${invoice.customer}-${invoice.product}`;
	// key value could be null, so nullish values should be evaluated as empty strings here
	if (map.has(key)) {
		const existing = map.get(key) as InvoicePlot;
		map.set(key, addToInvoicePlot(existing, invoice));
	} else {
		map.set(key, plot);
	}
}

/**
 * When combining invoices together due to a common entity type ('customer' or 'product')
 * it is highly likely that there are overlaps for the other entity.
 * e.g. common products are bought by different customers
 * Thus the customer or product may be returned as `null` instead of an explicit value.
 */
export function addToInvoiceRollup(
	rollup: InvoiceRollupWithInvoices,
	invoice: Invoice,
): InvoiceRollupWithInvoices {
	rollup.invoices.push(invoice.id);
	const costInCents = rollup.costInCents + invoice.costInCents;
	const revenueInCents = rollup.revenueInCents + invoice.revenueInCents;

	const evaluate = (a: string | null, b: string): string | null =>
		a === b ? a : null;

	const { valueInCents, percentage } = generateMargin(
		centsToDollars(revenueInCents),
		centsToDollars(costInCents),
	);

	return {
		id: rollup.id,
		costInCents,
		revenueInCents,
		product: evaluate(rollup.product, invoice.product),
		segment: evaluate(rollup.segment, invoice.segment),
		customer: evaluate(rollup.customer, invoice.customer),
		transactionAvgInCents: roundNumber(
			revenueInCents / rollup.invoices.length,
		),
		marginInCents: valueInCents,
		marginPercent: percentage,
		transactionCount: rollup.invoices.length,
		invoices: rollup.invoices,
		...DefaultInvoiceRollupEt(),
	};
}

function addToInvoicePlot(plot: InvoicePlot, invoice: Invoice): InvoicePlot {
	const costInCents = plot.costInCents + invoice.costInCents;
	const revenueInCents = plot.revenueInCents + invoice.revenueInCents;

	const { valueInCents, percentage } = generateMargin(
		centsToDollars(revenueInCents),
		centsToDollars(costInCents),
	);

	return {
		id: plot.id,
		costInCents,
		revenueInCents,
		product: plot.product,
		customer: plot.customer,
		marginInCents: valueInCents,
		marginPercent: percentage,
		coordinates: plot.coordinates,
	};
}

const DefaultInvoiceRollupEt = () => ({
	count80: 0,
	percent80: 0,
	revenueInCents80: 0,
	costInCents80: 0,
	marginInCents80: 0,
	marginPercent80: 0,
	count20: 0,
	percent20: 0,
	revenueInCents20: 0,
	costInCents20: 0,
	marginInCents20: 0,
	marginPercent20: 0,
	quartile: 0,
	quad: 0,
});

export const stripRollupInvoices = (
	list: InvoiceRollupWithInvoices[],
): InvoiceRollup[] =>
	list.map((rollup) => {
		// cast as partial to allow deletion of "non-optional" properties
		delete (rollup as Partial<InvoiceRollupWithInvoices>).invoices;
		return rollup as InvoiceRollup;
	});
